import { useCallback, useRef, useState } from 'react';
import { searchReportsPulldownApi } from '@intelligence/services/reportService';
import { SUGGEST_LIMIT } from '../_constants';
import type {
  ISuggestionItem,
  ISuggestionResponse,
  TSuggestionRawRecord,
} from './types';

export const useSuggestions = () => {
  const [suggestions, setSuggestions] = useState<ISuggestionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const latestKeywordRef = useRef<string>('');

  const fetchSuggestions = useCallback(async (keyword: string): Promise<void> => {
    const trimmed = keyword?.trim();
    latestKeywordRef.current = trimmed;
    if (!trimmed) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await searchReportsPulldownApi({
        ai_keyword: trimmed,
        page_size: SUGGEST_LIMIT,
      }) as ISuggestionResponse | TSuggestionRawRecord[];
      if (latestKeywordRef.current !== trimmed) return;
      let raw: TSuggestionRawRecord[] = [];
      if (Array.isArray(res)) {
        raw = res;
      } else if (Array.isArray(res.result)) {
        raw = res.result;
      } else if (Array.isArray(res.list)) {
        raw = res.list;
      }
      const list: ISuggestionItem[] = raw
        .map((item: TSuggestionRawRecord) => ({
          resource_id: typeof item === 'string' ? undefined : item?.resource_id,
          text:
            typeof item === 'string' ? item : (item?.text || item?.title || ''),
          system: typeof item === 'string' ? undefined : item?.system,
        }))
        .filter((item: ISuggestionItem) => !!item.text)
        .slice(0, SUGGEST_LIMIT);
      setSuggestions(list);
    } catch (err: unknown) {
      console.error('[ReportSearch] fetch suggestions failed:', err);
      if (latestKeywordRef.current !== trimmed) return;
      setSuggestions([]);
    } finally {
      if (latestKeywordRef.current === trimmed) {
        setLoading(false);
      }
    }
  }, []);

  const clearSuggestions = useCallback((): void => {
    latestKeywordRef.current = '';
    setSuggestions([]);
    setLoading(false);
  }, []);

  return { suggestions, loading, fetchSuggestions, clearSuggestions };
};
